/**
 * Amené et repli de matériel — la vue du responsable transport.
 *
 * *Demande de Chris, 04/09/2026 : « il faudrait créer un deuxième tableau de bord "Amené et
 * repli de matériel" […] faire apparaître jour par jour, en fonction de tous les CE de suivi,
 * tous les transports de matériel […] ce qui permettrait d'avoir une vision de l'ensemble des
 * machines à amener puis à ramener ou à transférer de chantier à chantier, ce qui permettrait
 * d'économiser énormément. »*
 *
 * ## Ce que cette vue fait, et ce qu'elle ne fait pas encore
 *
 * La liste des mouvements, jour par jour, tous chantiers confondus, **par machine nommée** et
 * **modifiable** — décaler d'un jour, retirer un mouvement, écrire une remarque.
 *
 * Et les **transferts possibles**, sur la ligne de chaque machine : le même engin replié d'un
 * chantier et attendu sur un autre dans les TROIS jours. Ce sont des propositions, jamais des
 * décisions -- *« le responsable transport vérifiera s'il peut transformer un repli en
 * transfert »* (Chris, 04/09/2026).
 *
 * Elle ne fait pas encore l'acceptation d'un transfert (qui l'enregistrerait), la carte, ni
 * l'affectation des camions et des chauffeurs.
 *
 * ## Les trois signes, et pourquoi ils sont si visibles
 *
 * *« Une flèche vers la droite quand c'est un amené, vers la gauche quand c'est un repli vers un
 * de nos sites, puis une flèche avec les chapeaux gauche et droite pour signifier un transfert
 * possible. »* C'est la forme que Chris a décrite, et elle est juste : ce qu'on lit dans cette
 * liste n'est pas un libellé, c'est **un sens de circulation**. Le mot « amené » demande une
 * lecture, la flèche ne la demande pas.
 *
 * ## Rien n'est stocké de ce qui s'affiche
 *
 * Chaque ligne est déduite des AFFECTATIONS du responsable à chaque affichage. Un chantier qui glisse
 * de trois jours emporte ses mouvements sans qu'une ligne soit réécrite. Ce qui est enregistré,
 * c'est ce que le responsable **corrige** — et l'écran montre alors les deux dates, la déduite et
 * la retenue, parce qu'un écart décidé doit rester visible.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { formaterDateLongue } from "@planif/moteur";
import {
  chargerMouvements,
  corrigerMouvement,
  defaireLeTransfert,
  retenirLeTransfert,
} from "./api";
import type {
  CreneauDeTransfert,
  Mouvement,
  PlanDesMouvements,
  TransfertPossible,
  TransfertRetenu,
} from "./api";
import {
  borneDuTravail,
  direLaFenetre,
  etatDeLaMoitie,
  direLaMoitieCorrigee,
  direLeCreneau,
  direLeMoment,
  momentDuVoyage,
} from "./momentDuMouvement";
import { PanneauPeriode } from "./Periode";
import type { Bornes } from "./Periode";

/**
 * Le signe d'un sens de circulation. Un transfert est un appariement, pas un sens.
 *
 * Le double chapeau apparaît dès qu'un transfert est POSSIBLE, et non seulement quand il est
 * décidé : c'est ce que Chris cherche du coin de l'œil en parcourant la liste -- *« une flèche
 * avec les chapeaux gauche et droite pour signifier un transfert possible »*. Un appariement déjà
 * accepté le porte aussi, et la ligne le dit en clair juste à côté.
 */
function signeDuSens(mouvement: Mouvement): string {
  if (mouvement.apparieA !== null || mouvement.transferts.length > 0) return "⇄";
  return mouvement.sens === "amene" ? "→" : "←";
}

function direLeSens(mouvement: Mouvement): string {
  if (mouvement.apparieA !== null) return "transfert décidé, de chantier à chantier";
  const base =
    mouvement.sens === "amene" ? "amené sur le chantier" : "repli vers un site du groupe";
  if (mouvement.transferts.length > 0) return `${base} — transfert possible, voir la date`;
  return base;
}

/**
 * Ce qu'une ligne raconte de sa date.
 *
 * Quand le responsable a décalé le mouvement, les DEUX dates s'affichent : celle que le planning
 * implique et celle qu'il a retenue. Ne montrer que la seconde ferait disparaître la décision —
 * et au prochain regard, personne ne saurait qu'il y a eu un choix.
 */
function direLaDate(mouvement: Mouvement): string {
  if (mouvement.decalageJours === 0) return "";
  const sens = mouvement.decalageJours < 0 ? "avancé" : "repoussé";
  const jours = Math.abs(mouvement.decalageJours);
  const deduite = mouvement.dateDeduite === null ? "?" : formaterDateLongue(mouvement.dateDeduite);
  return `${sens} de ${String(jours)} j — le planning dit ${deduite}`;
}

/**
 * Choisir le créneau d'un transfert — le geste que Chris a demandé deux fois.
 *
 * *« Ce qu'il faut, c'est que tu me donnes clairement les transferts possibles sur les trois
 * prochains jours, et le responsable transport doit pouvoir organiser lui-même à quelle date il
 * veut opérer le transfert »* (04/09/2026).
 *
 * ## Des boutons, et pas un calendrier
 *
 * La fenêtre fait au plus trois jours, donc au plus huit demi-journées. Les poser toutes à plat
 * met le choix sous les yeux : le responsable compare, il ne saisit pas. Un sélecteur de date
 * l'obligerait à savoir d'avance quels jours sont ouvrables et laquelle des deux moitiés est
 * encore possible — c'est justement ce que l'écran est là pour lui dire.
 *
 * Aucun créneau n'est présélectionné, et c'est le fond de la demande : l'application ne choisit
 * pas la date du voyage. Elle ne connaît ni la disponibilité du porte-engin ni celle du
 * chauffeur.
 */
function ChoixDuCreneau({
  creneaux,
  retenu,
  enAttente,
  onChoisir,
}: {
  creneaux: readonly CreneauDeTransfert[];
  retenu: CreneauDeTransfert | null;
  enAttente: boolean;
  onChoisir: (creneau: CreneauDeTransfert) => void;
}): JSX.Element {
  if (creneaux.length === 0) {
    return <span className="note note-ligne note--alerte">{direLaFenetre(creneaux)}</span>;
  }
  return (
    <span className="mvt__creneaux">
      <span className="note">{direLaFenetre(creneaux)} — choisir :</span>
      {creneaux.map((c) => {
        const estRetenu =
          retenu !== null && retenu.date === c.date && retenu.position === c.position;
        return (
          <button
            key={`${c.date}#${c.position}`}
            type="button"
            disabled={enAttente}
            className={estRetenu ? "actif" : undefined}
            title={
              estRetenu
                ? "C'est le créneau retenu"
                : "Retenir le transfert sur ce créneau — un seul voyage au lieu de deux"
            }
            onClick={() => {
              onChoisir(c);
            }}
          >
            {direLeCreneau(c)}
          </button>
        );
      })}
    </span>
  );
}

/**
 * Un transfert RETENU : une ligne, un voyage.
 *
 * L'amené de l'autre chantier a disparu de la liste — c'est le même voyage, et en montrer deux
 * lignes est exactement ce qui avait fait dire à Chris *« je ne comprends rien aux amenés et
 * repli »*. La ligne dit donc d'où la machine part, où elle va, et **quand le travail commence
 * là-bas** : c'est ce qui justifie le créneau qu'on a choisi.
 *
 * L'alerte, quand il y en a une, n'est pas un détail de présentation : un transfert décidé se
 * revérifie à chaque relecture, parce que les deux chantiers bougent après la décision. Rien ne
 * se déplace tout seul — l'écran dit ce qui ne va plus et nomme le geste.
 */
function TransfertOrganise({
  transfert,
  modifiable,
  enAttente,
  onDefaire,
}: {
  transfert: TransfertRetenu;
  modifiable: boolean;
  enAttente: boolean;
  onDefaire: () => void;
}): JSX.Element {
  return (
    <>
      <span className="note note-ligne mvt__transfert mvt__transfert--retenu">
        ⇄ <strong>transfert retenu vers {transfert.apo}</strong>
        {transfert.ville !== null && <span className="note"> · {transfert.ville}</span>}
        {transfert.dateTravail !== null && transfert.positionTravail !== null && (
          <>
            {" — travail dès "}
            {formaterDateLongue(transfert.dateTravail)} {direLeMoment(transfert.positionTravail)}
          </>
        )}
      </span>
      {transfert.alerte !== null && (
        <span className="note note-ligne note--alerte">⚠ {transfert.alerte}</span>
      )}
      {modifiable && (
        <button
          type="button"
          disabled={enAttente}
          title="Défaire l'appariement : les deux voyages redeviennent deux voyages, et la date choisie s'en va avec"
          onClick={onDefaire}
        >
          défaire le transfert
        </button>
      )}
    </>
  );
}

/** Une ligne de la liste : un mouvement, et les gestes qu'on peut poser dessus. */
function LigneMouvement({
  mouvement,
  modifiable,
  enAttente,
  onCorriger,
  onRetenir,
  onDefaire,
}: {
  mouvement: Mouvement;
  modifiable: boolean;
  enAttente: boolean;
  onCorriger: (
    mouvement: Mouvement,
    changement: {
      decalageJours?: number;
      retire?: boolean;
      remarque?: string | null;
      positionChoisie?: "matin" | "apres_midi" | null;
    },
  ) => void;
  onRetenir: (
    mouvement: Mouvement,
    autre: TransfertPossible | TransfertRetenu,
    creneau: CreneauDeTransfert,
  ) => void;
  onDefaire: (mouvement: Mouvement) => void;
}): JSX.Element {
  const voyage = momentDuVoyage(mouvement.position);
  const borne = borneDuTravail(mouvement);
  const moitieCorrigee = direLaMoitieCorrigee(mouvement);
  const moitie = etatDeLaMoitie(mouvement);
  /*
    Lié à une constante, et pas lu depuis `mouvement` dans le corps du rendu : la fonction
    passée au choix du créneau survit au rendu, et le compilateur ne peut pas garantir qu'un
    champ facultatif est encore là quand elle s'exécute. La constante, elle, ne bouge plus.
  */
  const retenu = mouvement.transfertRetenu;

  const classes = ["mvt__ligne"];
  if (mouvement.retire) classes.push("mvt__ligne--retiree");
  if (mouvement.decalageJours !== 0) classes.push("mvt__ligne--decalee");

  return (
    <tr className={classes.join(" ")}>
      <td className="mvt__apo">{mouvement.apo}</td>
      <td className="mvt__signe" title={direLeSens(mouvement)}>
        {signeDuSens(mouvement)}
      </td>
      <td>
        <strong>{mouvement.libelle}</strong>
        <span className="note"> {mouvement.code}</span>
        {/*
          La fenêtre de présence explique la date : « présente du J3 au J7 » dit pourquoi
          l'amené tombe au J3. Sans elle, le responsable ne peut que croire l'application.
        */}
        <span className="note note-ligne">
          poste{mouvement.postes.length > 1 ? "s" : ""} {mouvement.postes.join(", ")} · sur le chantier du jour{" "}
          {mouvement.fenetreDebut} au{" "}
          {mouvement.fenetreFin}
          {mouvement.ajoute && " · ajouté à la main"}
        </span>
      </td>
      {/*
        **Où livrer, et quand le travail commence** — *demande de Chris, 04/09/2026 : « tu dois
        aussi mentionner la ville où il faut livrer la machine et le jour de démarrage du travail
        prévu ».*

        La colonne annonçait le chantier d'où vient le mouvement, ce qui est l'information la
        moins utile à un chauffeur : sur un repli, il faut savoir où la machine RENTRE. Elle dit
        donc la destination — le chantier pour un amené, le site de repli pour un repli — et le
        chantier d'origine passe en note.

        *« Si dans les trois jours une machine n'est pas sollicitée sur un autre chantier, on la
        ramène à Pouydesseaux »* : ce site vient du `.env` de l'installation, jamais du code.
      */}
      <td>
        {mouvement.destination === null ? (
          <span className="note--alerte">
            aucun site de repli désigné — à renseigner dans la configuration
          </span>
        ) : (
          <strong>{mouvement.destination}</strong>
        )}
        {mouvement.sens === "repli" && (
          <span className="note note-ligne">
            depuis {mouvement.chantierNom}
            {mouvement.ville !== null && ` · ${mouvement.ville}`}
          </span>
        )}
      </td>
      <td className="mvt__date">
        {mouvement.date === null ? "—" : formaterDateLongue(mouvement.date)}
        {/*
          **La demi-journée du voyage, et celle du travail** — *demande de Chris, 04/09/2026 :
          « il faut être beaucoup plus précis, tu dois indiquer à quelle heure la machine est
          prévue d'arrêter de travailler ; si la machine est affectée sur l'après-midi alors il
          faut proposer transport lendemain matin ».*

          Une date seule ne suffisait pas : elle ne disait pas si l'on peut charger l'engin le
          soir même. La ligne annonce donc le moment du voyage, puis le moment où le travail
          commence ou s'arrête — c'est ce second qui explique le premier.
        */}
        <span className={`mvt__moment${voyage.alerte ? " note--alerte" : ""}`}>{voyage.texte}</span>
        {moitieCorrigee !== null && (
          <span className="note note-ligne note--alerte">{moitieCorrigee}</span>
        )}
        {borne !== null && mouvement.dateTravail !== null && (
          <span className="note note-ligne">
            {borne.prefixe}
            {formaterDateLongue(mouvement.dateTravail)} {borne.moment}
          </span>
        )}
        {direLaDate(mouvement) !== "" && (
          <span className="note note-ligne note--alerte">{direLaDate(mouvement)}</span>
        )}
        {/*
          **Le transfert RETENU se lit avant les propositions**, parce qu'il n'y en a plus : la
          ligne est décidée, et l'amené de l'autre chantier a disparu de la liste.
        */}
        {retenu !== null && (
          <>
            <TransfertOrganise
              transfert={retenu}
              modifiable={modifiable}
              enAttente={enAttente}
              onDefaire={() => {
                onDefaire(mouvement);
              }}
            />
            {modifiable && (
              <ChoixDuCreneau
                creneaux={retenu.creneaux}
                retenu={
                  mouvement.date === null || mouvement.position === null
                    ? null
                    : { date: mouvement.date, position: mouvement.position }
                }
                enAttente={enAttente}
                onChoisir={(creneau) => {
                  onRetenir(mouvement, retenu, creneau);
                }}
              />
            )}
          </>
        )}
        {mouvement.transferts.map((t) => (
          <span
            key={t.cle}
            className={`note note-ligne mvt__transfert${t.ecartJours === 0 ? " mvt__transfert--franc" : ""}`}
          >
            ⇄ transfert possible {t.role === "depart" ? "vers" : "depuis"} <strong>{t.apo}</strong>
            {t.ville !== null && <span className="note"> · {t.ville}</span>}
            {" — "}
            {/*
              **Une FOURCHETTE, jamais une date d'arrivée.**

              *Correction de Chris, 04/09/2026 : « on ne peut pas dire que la machine arrive le
              jour où elle quitte un chantier ; ce qu'il faut, c'est que tu me donnes clairement
              les transferts possibles sur les trois prochains jours, et le responsable transport
              doit pouvoir organiser lui-même à quelle date il veut opérer le transfert. »*

              L'écran annonce donc les deux bornes et rien entre elles. La date du voyage est une
              décision d'organisation — le porte-engin est libre tel jour, pas tel autre — et
              l'outil n'en sait rien. *J'avais d'abord annoncé une arrivée le jour de la
              libération et un temps d'attente : c'était une supposition, et elle inventait un
              coût que personne n'a constaté.*
            */}
            {t.role === "depart" ? (
              <>
                libérée ici, <strong>attendue là-bas le {formaterDateLongue(t.date)}</strong>
              </>
            ) : (
              <>
                attendue ici, <strong>libérée là-bas le {formaterDateLongue(t.date)}</strong>
              </>
            )}
            {t.dateTravail !== null && t.positionTravail !== null && (
              <span className="note note-ligne">
                {t.role === "depart" ? "travail dès " : "travail jusqu'au "}
                {formaterDateLongue(t.dateTravail)} {direLeMoment(t.positionTravail)}
              </span>
            )}
            {/*
              **Et le geste qui RETIENT le transfert, avec sa date.**

              *« Le responsable transport doit pouvoir organiser lui-même à quelle date il veut
              opérer le transfert »* (Chris, 04/09/2026).

              Offert des deux côtés du couple, sur la ligne du repli comme sur celle de l'amené :
              le responsable organise depuis celle qu'il a sous les yeux, et l'appariement écrit
              est le même. Ce qui change, c'est seulement laquelle des deux moitiés est le départ
              — et c'est le rôle qui le dit.
            */}
            {modifiable && (
              <ChoixDuCreneau
                creneaux={t.creneaux}
                retenu={null}
                enAttente={enAttente}
                onChoisir={(creneau) => {
                  onRetenir(mouvement, t, creneau);
                }}
              />
            )}
          </span>
        ))}
      </td>
      <td className="mvt__gestes">
        {modifiable && (
          <>
            <button
              type="button"
              disabled={enAttente}
              title="Avancer ce mouvement d'un jour ouvré"
              onClick={() => {
                onCorriger(mouvement, { decalageJours: mouvement.decalageJours - 1 });
              }}
            >
              −1 j
            </button>
            <button
              type="button"
              disabled={enAttente}
              title="Repousser ce mouvement d'un jour ouvré"
              onClick={() => {
                onCorriger(mouvement, { decalageJours: mouvement.decalageJours + 1 });
              }}
            >
              +1 j
            </button>
            {mouvement.decalageJours !== 0 && (
              <button
                type="button"
                disabled={enAttente}
                title="Revenir à la date que le planning implique"
                onClick={() => {
                  onCorriger(mouvement, { decalageJours: 0 });
                }}
              >
                au planning
              </button>
            )}
            {/*
              **Préciser la demi-journée du voyage** — *demande de Chris, 04/09/2026 : « mets le
              bouton pour préciser la demi-journée ».*

              Deux boutons et non une bascule : une bascule ne saurait pas dire « rien de
              choisi ». Or c'est justement l'état d'un mouvement ajouté à la main, où aucune
              règle ne donne la moitié du jour — et l'afficher au matin par défaut se lirait
              comme une décision que personne n'a prise.

              Sur un mouvement déduit, ils CORRIGENT la règle : « finalement on charge cet engin
              le matin ». Le troisième bouton lui rend la main, et il n'apparaît que là où il y a
              une règle où revenir.
            */}
            <button
              type="button"
              disabled={enAttente}
              className={moitie.choisie === "matin" ? "actif" : undefined}
              title="Charger cet engin le MATIN"
              onClick={() => {
                onCorriger(mouvement, { positionChoisie: "matin" });
              }}
            >
              matin
            </button>
            <button
              type="button"
              disabled={enAttente}
              className={moitie.choisie === "apres_midi" ? "actif" : undefined}
              title="Charger cet engin l'APRÈS-MIDI"
              onClick={() => {
                onCorriger(mouvement, { positionChoisie: "apres_midi" });
              }}
            >
              après-midi
            </button>
            {moitie.peutRevenirAuPlanning && (
              <button
                type="button"
                disabled={enAttente}
                title="Rendre la main au planning : la demi-journée redeviendra celle qu'implique le travail"
                onClick={() => {
                  onCorriger(mouvement, { positionChoisie: null });
                }}
              >
                moitié au planning
              </button>
            )}
            <button
              type="button"
              disabled={enAttente}
              className={mouvement.retire ? "actif" : undefined}
              title={
                mouvement.retire
                  ? "Remettre ce mouvement : il redeviendra à organiser"
                  : "Ce mouvement n'a pas lieu — la machine est déjà sur place, ou elle reste"
              }
              onClick={() => {
                onCorriger(mouvement, { retire: !mouvement.retire });
              }}
            >
              {mouvement.retire ? "remettre" : "sans objet"}
            </button>
            <button
              type="button"
              disabled={enAttente}
              title="Écrire pourquoi — c'est la seule trace d'une décision"
              onClick={() => {
                const saisie = globalThis.prompt("Remarque sur ce mouvement", mouvement.remarque ?? "");
                if (saisie === null) return;
                onCorriger(mouvement, { remarque: saisie.trim() === "" ? null : saisie.trim() });
              }}
            >
              remarque
            </button>
          </>
        )}
        {mouvement.remarque !== null && (
          <span className="note note-ligne">« {mouvement.remarque} »</span>
        )}
      </td>
    </tr>
  );
}

/**
 * Ce que l'écran ne sait pas, dit en toutes lettres.
 *
 * *Cadre 3 du 01/09/2026 : « compté et affiché, jamais un tableau vide ».* Un écran
 * d'organisation incomplet se lit « tout va bien » — c'est le pire mode de panne de celui-ci.
 */
function CeQuOnIgnore({ plan }: { plan: PlanDesMouvements }): JSX.Element | null {
  const { ignore } = plan;
  const points: string[] = [];
  if (ignore.machinesSansEngin > 0) {
    points.push(
      `${String(ignore.machinesSansEngin)} machine(s) chiffrée(s) sur lesquelles aucun engin ` +
        "réel n'est affecté : on ne sait pas lequel monter",
    );
  }
  if (ignore.chantiersSansDate > 0) {
    points.push(
      `${String(ignore.chantiersSansDate)} chantier(s) validé(s) sans date de démarrage : ` +
        "leurs mouvements n'ont pas de jour",
    );
  }
  if (ignore.machinesNonTranchees.length > 0) {
    points.push(
      `${String(ignore.machinesNonTranchees.length)} machine(s) dont le référentiel ne dit pas si ` +
        `elles montent sur porte-engin — ${ignore.machinesNonTranchees.join(", ")}. ` +
        "À trancher dans Référentiels → Matériel, colonne Acheminement.",
    );
  }
  if (ignore.affectationsSansTravail > 0) {
    points.push(
      `${String(ignore.affectationsSansTravail)} affectation(s) posée(s) un jour où aucune barre ` +
        "ne travaille : elles ne produisent aucun mouvement, et sont presque toujours un oubli",
    );
  }
  if (ignore.appariementsARevoir > 0) {
    points.push(
      `${String(ignore.appariementsARevoir)} transfert(s) retenu(s) dont l'appariement ne se ` +
        "retrouve plus : l'autre moitié a disparu du planning. À défaire et à reprendre.",
    );
  }
  if (ignore.correctionsOrphelines > 0) {
    points.push(
      `${String(ignore.correctionsOrphelines)} correction(s) ne désignent plus aucun mouvement : ` +
        "le planning a changé depuis",
    );
  }
  if (points.length === 0) return null;

  return (
    <div className="mvt__ignore">
      <strong>Ce que ce tableau ne sait pas</strong>
      <ul>
        {points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

/** Les mouvements groupés par jour — c'est la question du responsable : que fait-on demain ? */
function parJour(mouvements: readonly Mouvement[]): readonly { jour: string; lignes: readonly Mouvement[] }[] {
  const index = new Map<string, Mouvement[]>();
  for (const m of mouvements) {
    if (m.date === null) continue;
    const liste = index.get(m.date) ?? [];
    liste.push(m);
    index.set(m.date, liste);
  }
  return [...index.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([jour, lignes]) => ({ jour, lignes }));
}

export function MouvementsMateriel({ modifiable }: { modifiable: boolean }): JSX.Element {
  const [plan, setPlan] = useState<PlanDesMouvements | null>(null);
  /**
   * Les bornes DEMANDÉES, vides tant que le responsable n'a rien saisi.
   *
   * Elles ne se remplissent jamais de ce que le serveur rend : c'est lui qui décide l'horizon
   * par défaut, et le recopier ici transformerait un défaut glissant en deux dates figées.
   * Le panneau, lui, affiche la période *retenue*, qui vient du plan.
   */
  const [demandees, setDemandees] = useState<Bornes>({ debut: "", fin: "" });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const lu = await chargerMouvements({ debut: demandees.debut, fin: demandees.fin });
      setPlan(lu.plan);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setChargement(false);
    }
  }, [demandees]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const jours = useMemo(() => parJour(plan?.mouvements ?? []), [plan]);

  /**
   * Retenir un transfert au créneau choisi — ou en changer, ce qui est le même geste.
   *
   * *« Le responsable transport doit pouvoir organiser lui-même à quelle date il veut opérer le
   * transfert »* (Chris, 04/09/2026).
   *
   * Le rôle dit laquelle des deux moitiés est le DÉPART : sur une ligne de repli, c'est elle ;
   * sur une ligne d'amené, c'est l'autre. Le serveur écrit toujours la date sur le repli, parce
   * qu'un transfert est un seul voyage et qu'un voyage n'a qu'une date.
   */
  const retenir = async (
    mouvement: Mouvement,
    autre: TransfertPossible | TransfertRetenu,
    creneau: CreneauDeTransfert,
  ): Promise<void> => {
    const moi = {
      chantierId: mouvement.chantierId,
      ressourceId: mouvement.ressourceId,
      fenetre: mouvement.fenetre,
    };
    const lui = {
      chantierId: autre.chantierId,
      ressourceId: autre.ressourceId,
      fenetre: autre.fenetre,
    };
    const surLeRepli = mouvement.sens === "repli";
    setEnAttente(true);
    try {
      await retenirLeTransfert({
        depart: surLeRepli ? moi : lui,
        arrivee: surLeRepli ? lui : moi,
        date: creneau.date,
        position: creneau.position,
      });
      await charger();
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnAttente(false);
    }
  };

  /**
   * Défaire un transfert : les deux voyages redeviennent deux voyages.
   *
   * Toujours depuis la ligne du repli, puisque c'est elle qui porte le transfert retenu — la
   * moitié arrivée n'est plus affichée.
   */
  const defaire = async (mouvement: Mouvement): Promise<void> => {
    setEnAttente(true);
    try {
      await defaireLeTransfert({
        chantierId: mouvement.chantierId,
        ressourceId: mouvement.ressourceId,
        fenetre: mouvement.fenetre,
      });
      await charger();
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnAttente(false);
    }
  };

  const corriger = async (
    mouvement: Mouvement,
    changement: {
      decalageJours?: number;
      retire?: boolean;
      remarque?: string | null;
      positionChoisie?: "matin" | "apres_midi" | null;
    },
  ): Promise<void> => {
    setEnAttente(true);
    try {
      await corrigerMouvement({
        chantierId: mouvement.chantierId,
        ressourceId: mouvement.ressourceId,
        fenetre: mouvement.fenetre,
        sens: mouvement.sens,
        decalageJours: changement.decalageJours ?? mouvement.decalageJours,
        retire: changement.retire ?? mouvement.retire,
        remarque: changement.remarque === undefined ? mouvement.remarque : changement.remarque,
        /*
          **L'état COMPLET part à chaque correction**, et pas seulement ce qui change.

          L'écriture est un `UPSERT` : elle réécrit la ligne entière. Omettre la
          demi-journée ferait donc effacer, d'un clic sur « +1 j », le créneau qu'un
          transfert avait retenu — sans qu'aucun message ne le dise.
        */
        positionChoisie:
          changement.positionChoisie === undefined
            ? mouvement.moitieChoisie
            : changement.positionChoisie,
      });
      /*
        On relit tout plutôt que de modifier la ligne sur place.

        Une correction peut faire sortir un mouvement de la période affichée, ou en faire
        entrer un autre — le décalage se pose après le filtrage de l'horizon, côté serveur.
        Rafraîchir la seule ligne laisserait un écran qui n'est plus celui que le serveur
        rendrait, et c'est exactement le genre d'écart qu'on ne voit qu'au rechargement
        suivant.
      */
      await charger();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnAttente(false);
    }
  };

  return (
    <section className="mvt">
      <h3 className="plan__nom-tableau">Amené et repli de matériel</h3>
      <p className="etat">
        Les machines à monter, à ramener ou à transférer, jour par jour et tous chantiers
        confondus. <strong>Rien n'est saisi ici</strong> : chaque mouvement se déduit des barres
        du conducteur par la règle du trou — un jour ouvré d'inactivité, la machine reste ; plus
        d'un jour, elle redescend. Vos corrections, elles, sont enregistrées.
      </p>

      <PanneauPeriode
        affichee={plan === null ? null : { debut: plan.debut, fin: plan.fin }}
        chargement={chargement}
        onBorner={setDemandees}
        onDefaut={() => {
          setDemandees({ debut: "", fin: "" });
        }}
      />

      {erreur !== null && <p className="alerte">{erreur}</p>}
      {chargement && <p className="etat">Lecture des plannings…</p>}

      {plan !== null && <CeQuOnIgnore plan={plan} />}

      {plan !== null && !chargement && jours.length === 0 && (
        <p className="etat">
          Aucun mouvement de matériel sur cette période. Ce n'est pas forcément un défaut : la
          règle du trou a besoin des barres du Gantt, et un chantier dont la contre-étude n'est
          pas encore validée n'entre pas dans ce tableau.
        </p>
      )}

      {jours.map(({ jour, lignes }) => (
        <article key={jour} className="plan__jour-bloc">
          <h4>{formaterDateLongue(jour)}</h4>
          <table>
            <thead>
              <tr>
                <th>Affaire</th>
                <th />
                <th>Machine</th>
                <th>À livrer</th>
                <th>Jour retenu</th>
                <th>Organisation</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((m) => (
                <LigneMouvement
                  key={m.cle}
                  mouvement={m}
                  modifiable={modifiable}
                  enAttente={enAttente}
                  onCorriger={(mvt, changement) => {
                    void corriger(mvt, changement);
                  }}
                  onRetenir={(mvt, autre, creneau) => {
                    void retenir(mvt, autre, creneau);
                  }}
                  onDefaire={(mvt) => {
                    void defaire(mvt);
                  }}
                />
              ))}
            </tbody>
          </table>
        </article>
      ))}
    </section>
  );
}
